import { useState } from "react";
import {
  Loader2, Plus, Trash2, ArrowUp, ArrowDown, Sparkles, GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";

const STROKE = 1.5;

export const REQUIREMENT_TYPES = [
  { value: "experience_years", label: "Experience (years)" },
  { value: "education", label: "Education" },
  { value: "skill", label: "Skill" },
  { value: "domain", label: "Domain" },
  { value: "certification", label: "Certification" },
  { value: "language", label: "Language" },
  { value: "location", label: "Location" },
  { value: "other", label: "Other" },
];

const TYPE_LABEL = Object.fromEntries(REQUIREMENT_TYPES.map((t) => [t.value, t.label]));

function WeightToggle({ value, disabled, onChange }) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-ink-200" data-testid="req-weight-toggle">
      {[["must_have", "Must-have"], ["nice_to_have", "Nice-to-have"]].map(([v, lbl]) => (
        <button
          key={v}
          disabled={disabled}
          onClick={() => value !== v && onChange(v)}
          className={`px-2.5 py-1 text-[12px] font-medium transition ${
            value === v
              ? v === "must_have"
                ? "bg-emerald-800 text-white"
                : "bg-ink-700 text-white"
              : "bg-white text-ink-500 hover:bg-ink-50"
          } disabled:opacity-60`}
          data-testid={`req-weight-${v}`}
        >
          {lbl}
        </button>
      ))}
    </div>
  );
}

function RequirementRow({ req, index, total, canEdit, onMutated, onMove }) {
  const [label, setLabel] = useState(req.label);
  const [busy, setBusy] = useState(false);

  const patch = async (body) => {
    setBusy(true);
    try {
      await api.patch(`/jd-requirements/${req.jd_requirement_id}`, body);
      onMutated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update requirement");
    } finally {
      setBusy(false);
    }
  };

  const saveLabel = () => {
    const next = label.trim();
    if (!next || next === req.label) {
      setLabel(req.label);
      return;
    }
    patch({ label: next });
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/jd-requirements/${req.jd_requirement_id}`);
      onMutated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove requirement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-2 border-b border-ink-100 px-3 py-2.5 ${
        req.is_active ? "" : "opacity-55"
      }`}
      data-testid={`req-row-${req.jd_requirement_id}`}
    >
      <GripVertical className="h-3.5 w-3.5 shrink-0 text-ink-300" strokeWidth={STROKE} />
      <input
        value={label}
        disabled={!canEdit || busy}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={saveLabel}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className="min-w-[180px] flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 text-[13px] text-ink-900 hover:border-ink-200 focus:border-emerald-800/40 focus:bg-white focus:outline-none disabled:cursor-default"
        data-testid={`req-label-${req.jd_requirement_id}`}
      />
      <div className="w-[168px]" data-testid={`req-type-wrap-${req.jd_requirement_id}`}>
        <Select
          value={req.requirement_type}
          disabled={!canEdit || busy}
          onValueChange={(v) => patch({ requirement_type: v })}
        >
          <SelectTrigger className="h-8 text-[12.5px]" data-testid={`req-type-${req.jd_requirement_id}`}>
            <SelectValue>{TYPE_LABEL[req.requirement_type]}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {REQUIREMENT_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value} className="text-[12.5px]">
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <WeightToggle
        value={req.weight}
        disabled={!canEdit || busy}
        onChange={(v) => patch({ weight: v })}
      />
      {canEdit && (
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => patch({ is_active: !req.is_active })}
            disabled={busy}
            title={req.is_active ? "Deactivate (exclude from matching)" : "Activate"}
            className="rounded-md px-2 py-1 text-[11px] font-medium text-ink-500 hover:bg-ink-50"
            data-testid={`req-active-${req.jd_requirement_id}`}
          >
            {req.is_active ? "Active" : "Off"}
          </button>
          <button
            onClick={() => onMove(index, "up")}
            disabled={busy || index === 0}
            className="rounded-md p-1 text-ink-400 hover:bg-ink-50 disabled:opacity-30"
            data-testid={`req-up-${req.jd_requirement_id}`}
            aria-label="Move up"
          >
            <ArrowUp className="h-3.5 w-3.5" strokeWidth={STROKE} />
          </button>
          <button
            onClick={() => onMove(index, "down")}
            disabled={busy || index === total - 1}
            className="rounded-md p-1 text-ink-400 hover:bg-ink-50 disabled:opacity-30"
            data-testid={`req-down-${req.jd_requirement_id}`}
            aria-label="Move down"
          >
            <ArrowDown className="h-3.5 w-3.5" strokeWidth={STROKE} />
          </button>
          <button
            onClick={remove}
            disabled={busy}
            className="rounded-md p-1 text-ink-400 hover:bg-red-50 hover:text-red-600"
            data-testid={`req-delete-${req.jd_requirement_id}`}
            aria-label="Delete requirement"
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={STROKE} />
          </button>
        </div>
      )}
      {req.source === "recruiter_added" && (
        <span className="rounded bg-ink-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-ink-400">
          added
        </span>
      )}
    </div>
  );
}

function AddRequirement({ jobDescriptionId, onMutated }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [type, setType] = useState("skill");
  const [weight, setWeight] = useState("nice_to_have");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      await api.post(`/jd-requirements`, {
        job_description_id: jobDescriptionId,
        label: label.trim(),
        requirement_type: type,
        parsed_params: {},
        weight,
      });
      setLabel("");
      setType("skill");
      setWeight("nice_to_have");
      setOpen(false);
      onMutated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add requirement");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="m-3 inline-flex items-center gap-1.5 rounded-md border border-dashed border-ink-300 px-3 py-1.5 text-[12.5px] font-medium text-ink-600 hover:border-emerald-800/40 hover:text-emerald-900"
        data-testid="req-add-open"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={STROKE} /> Add a requirement
      </button>
    );
  }

  return (
    <div className="m-3 flex flex-wrap items-center gap-2 rounded-md border border-ink-200 bg-ink-50/60 p-3" data-testid="req-add-form">
      <input
        autoFocus
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder="e.g. 3+ years Kubernetes"
        className="min-w-[200px] flex-1 rounded-md border border-ink-200 px-2.5 py-1.5 text-[13px] focus:border-emerald-800/40 focus:outline-none"
        data-testid="req-add-label"
      />
      <div className="w-[168px]">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-9 text-[12.5px]" data-testid="req-add-type">
            <SelectValue>{TYPE_LABEL[type]}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {REQUIREMENT_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value} className="text-[12.5px]">{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <WeightToggle value={weight} onChange={setWeight} />
      <button
        onClick={submit}
        disabled={busy || !label.trim()}
        className="inline-flex items-center gap-1.5 rounded-md bg-emerald-800 px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-emerald-900 disabled:opacity-50"
        data-testid="req-add-submit"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={STROKE} /> : "Add"}
      </button>
      <button
        onClick={() => setOpen(false)}
        className="rounded-md px-2 py-1.5 text-[12.5px] text-ink-500 hover:bg-ink-100"
        data-testid="req-add-cancel"
      >
        Cancel
      </button>
    </div>
  );
}

export default function RequirementEditor({ jobDescriptionId, requirements, canEdit, onMutated, onExtract, extracting }) {
  const active = requirements.filter((r) => r.is_active);

  const move = async (index, dir) => {
    const target = dir === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= requirements.length) return;
    const ids = requirements.map((r) => r.jd_requirement_id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    try {
      await api.post(`/job-descriptions/${jobDescriptionId}/requirements/reorder`, { ordered_ids: ids });
      onMutated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reorder");
    }
  };

  const bulkWeight = async (weight) => {
    try {
      await api.post(`/job-descriptions/${jobDescriptionId}/requirements/bulk-weight`, {
        job_description_id: jobDescriptionId, weight,
      });
      onMutated();
      toast.success(weight === "must_have" ? "All set to must-have" : "All set to nice-to-have");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    }
  };

  return (
    <div data-testid="requirement-editor">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="hp-section-label">
          Requirements · {active.length} active of {requirements.length}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && requirements.length > 0 && (
            <div className="flex items-center gap-1 text-[11.5px] text-ink-400">
              <span>Set all:</span>
              {[["must_have", "Must-have"], ["nice_to_have", "Nice-to-have"]].map(([v, lbl]) => (
                <button
                  key={v}
                  onClick={() => bulkWeight(v)}
                  className="rounded-md border border-ink-200 bg-white px-2 py-1 font-medium text-ink-600 hover:border-emerald-800/40 hover:text-emerald-900"
                  data-testid={`req-bulk-${v}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          )}
          {canEdit && requirements.length === 0 && (
            <button
              onClick={onExtract}
              disabled={extracting}
              className="inline-flex items-center gap-2 rounded-md bg-emerald-800 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-900 disabled:opacity-60"
              data-testid="extract-requirements-button"
            >
              {extracting ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> : <Sparkles className="h-4 w-4" strokeWidth={STROKE} />}
              {extracting ? "Extracting…" : "Extract from JD"}
            </button>
          )}
        </div>
      </div>

      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900" data-testid="criteria-control-banner">
        <strong>You control the criteria.</strong> Hiretape checks CVs against the requirements you confirm here — nothing else.
      </div>

      <div className="hp-card mt-3 overflow-hidden">
        {requirements.length === 0 ? (
          <div className="p-8 text-center" data-testid="requirements-empty">
            <p className="text-[13px] text-ink-500">
              No requirements yet. Extract them from the job description, or add your own.
            </p>
          </div>
        ) : (
          requirements.map((req, i) => (
            <RequirementRow
              key={req.jd_requirement_id}
              req={req}
              index={i}
              total={requirements.length}
              canEdit={canEdit}
              onMutated={onMutated}
              onMove={move}
            />
          ))
        )}
        {canEdit && <AddRequirement jobDescriptionId={jobDescriptionId} onMutated={onMutated} />}
      </div>
    </div>
  );
}
